import { createContext, useState, useCallback, useEffect } from "react";
import ExportService from "../services/ExportService";
import { useDocumentVisibility } from "@mantine/hooks";

export const ExportsContext = createContext(null);

export const ExportsProvider = ({ children }) => {
  const documentState = useDocumentVisibility();
  const isDocumentVisible = documentState === "visible";
  const [exportsList, setExportsList] = useState([]);
  const [exportsCount, setExportsCount] = useState(0);
  const [activeExportsCount, setActiveExportsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [highlightExportId, setHighlightExportId] = useState(null);
  const [menuOpened, setMenuOpened] = useState(false);

  const fetchExports = useCallback(
    async ({ page = 1, page_size = 25 } = {}) => {
      setLoading(true);
      try {
        const { results, count } = await ExportService.getExports({
          page,
          page_size,
        });
        setExportsList(results);
        setExportsCount(count);
      } catch (error) {
        console.error("Error fetching exports", error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateExport = useCallback(async (exportId, payload) => {
    try {
      const updatedExport = await ExportService.patchExport(exportId, payload);
      setExportsList((prevExports) =>
        prevExports.map((exp) =>
          exp.id === updatedExport.id ? updatedExport : exp
        )
      );
      return updatedExport;
    } catch (error) {
      console.error("Error updating export", error);
      throw error;
    }
  }, []);

  const createExport = useCallback(async (payload) => {
    try {
      const newExport = await ExportService.createExport(payload);
      setExportsList((prevExports) => [newExport, ...prevExports]);
      setExportsCount((prevCount) => prevCount + 1);
      return newExport;
    } catch (error) {
      console.error("Error creating export", error);
      throw error;
    }
  }, []);

  useEffect(() => {
    fetchExports();
  }, [fetchExports]);

  useEffect(() => {
    const count = exportsList.filter((exp) =>
      ["PE", "PR"].includes(exp.status)
    ).length;
    setActiveExportsCount(count);
  }, [exportsList]);

  useEffect(() => {
    if (activeExportsCount > 0) {
      const interval = setInterval(() => {
        if (!isDocumentVisible) return;
        fetchExports();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [activeExportsCount, fetchExports]);

  return (
    <ExportsContext.Provider
      value={{
        exportsList,
        exportsCount,
        activeExportsCount,
        loading,
        fetchExports,
        updateExport,
        createExport,
        highlightExportId,
        setHighlightExportId,
        menuOpened,
        setMenuOpened,
      }}
    >
      {children}
    </ExportsContext.Provider>
  );
};
